import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchProjectSummaryData,
  fetchChangeOrdersData,
  fetchSpendSummaryData,
  type ProjectSummary,
  type ChangeOrder,
  type VendorSpendSummary,
} from "../../services/api/reportingService";

// ─── Async Thunks ────────────────────────────────────────────────────
export const fetchProjectSummary = createAsyncThunk(
  "reporting/fetchProjectSummary",
  async (projectName: string | undefined, { rejectWithValue }) => {
    try {
      return await fetchProjectSummaryData(projectName);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch project summary");
    }
  }
);

export const fetchChangeOrders = createAsyncThunk(
  "reporting/fetchChangeOrders",
  async (projectName: string | undefined, { rejectWithValue }) => {
    try {
      return await fetchChangeOrdersData(projectName);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch change orders");
    }
  }
);

export const fetchSpendSummary = createAsyncThunk(
  "reporting/fetchSpendSummary",
  async (projectName: string | undefined, { rejectWithValue }) => {
    try {
      return await fetchSpendSummaryData(projectName);
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch spend summary");
    }
  }
);

// ─── State ───────────────────────────────────────────────────────────
interface ReportingState {
  projectSummary: ProjectSummary | null;
  changeOrders: ChangeOrder[];
  spendSummary: VendorSpendSummary[];
  projectSummaryStatus: "idle" | "loading" | "succeeded" | "failed";
  changeOrdersStatus: "idle" | "loading" | "succeeded" | "failed";
  spendSummaryStatus: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ReportingState = {
  projectSummary: null,
  changeOrders: [],
  spendSummary: [],
  projectSummaryStatus: "idle",
  changeOrdersStatus: "idle",
  spendSummaryStatus: "idle",
  error: null,
};

// ─── Slice ───────────────────────────────────────────────────────────
const reportingSlice = createSlice({
  name: "reporting",
  initialState,
  reducers: {
    resetReporting: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Project Summary
      .addCase(fetchProjectSummary.pending, (state) => {
        state.projectSummaryStatus = "loading";
      })
      .addCase(fetchProjectSummary.fulfilled, (state, action) => {
        state.projectSummaryStatus = "succeeded";
        state.projectSummary = action.payload;
      })
      .addCase(fetchProjectSummary.rejected, (state, action) => {
        state.projectSummaryStatus = "failed";
        state.error = action.payload as string;
      })
      // Change Orders
      .addCase(fetchChangeOrders.pending, (state) => {
        state.changeOrdersStatus = "loading";
      })
      .addCase(fetchChangeOrders.fulfilled, (state, action) => {
        state.changeOrdersStatus = "succeeded";
        state.changeOrders = action.payload;
      })
      .addCase(fetchChangeOrders.rejected, (state, action) => {
        state.changeOrdersStatus = "failed";
        state.error = action.payload as string;
      })
      // Spend Summary
      .addCase(fetchSpendSummary.pending, (state) => {
        state.spendSummaryStatus = "loading";
      })
      .addCase(fetchSpendSummary.fulfilled, (state, action) => {
        state.spendSummaryStatus = "succeeded";
        state.spendSummary = action.payload;
      })
      .addCase(fetchSpendSummary.rejected, (state, action) => {
        state.spendSummaryStatus = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { resetReporting } = reportingSlice.actions;
export default reportingSlice.reducer;
